import React from "react";
import Sidebar from "./Sidebar";
import { cn } from "../../lib/utils";

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  return (
    <div className="flex min-h-dvh bg-bg">
      <Sidebar />
      <main className="relative flex-1 overflow-auto">
        {/* Ambient brand glow at the top, matching the assurance workspace */}
        <div
          aria-hidden="true"
          className="asr-glow pointer-events-none absolute inset-x-0 top-0 h-64"
        />
        <div className="relative">{children}</div>
      </main>
    </div>
  );
}

// Page header component
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: React.ReactNode;
}) {
  return (
    <header className="mb-7 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-text">{title}</h1>
        <div
          aria-hidden="true"
          className="mt-2 h-[3px] w-11 rounded-full bg-gradient-to-r from-brand to-focus/40"
        />
        {description && (
          <p className="mt-2 text-sm leading-6 text-muted">{description}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-3">{actions}</div>}
    </header>
  );
}

// Page container
export function PageContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {children}
    </div>
  );
}

// Section component
export function Section({
  title,
  description,
  actions,
  children,
  className,
}: {
  title?: string;
  description?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("space-y-4", className)}>
      {(title || actions) && (
        <div className="flex items-center justify-between gap-4">
          <div>
            {title && (
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted">
                {title}
              </h2>
            )}
            {description && (
              <p className="mt-0.5 text-sm text-muted">{description}</p>
            )}
          </div>
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}
